<script setup lang="ts">
import { ref, computed } from 'vue';
import LoadingOverlay from '@/components/common/LoadingOverlay.vue';
import { ltcsApi, commonWageApi } from '@/services/api';
import { formatLaborCostJobCategory } from '@/policies';
import {
  getCommonWageLedgerGrossPay,
  getCommonWageLedgerEmployerInsurance,
  getCommonWageLedgerRetirement,
  getCommonLaborCostJobCategoryOrder,
} from '@/policies/commonWageSettingPolicy';
import type { CommonWageListRow } from '@/types/commonWage';

interface LaborCostRow {
  yearMonth?: string;
  facilityType?: string;
  totalCost?: number;
  totalAdd?: number;
  laborCost?: number;
  laborAdd?: number;
  nonLaborCost?: number;
  nonLaborAdd?: number;
  selfPayCost?: number;
}

interface JobSummaryRow {
  category: string;
  count: number;
  grossPay: number;
  employerInsurance: number;
  retirement: number;
}

const props = defineProps<{
  memberCode: string;
  year: number;
  month: number;
  bizGubun?: string;
}>();

const showModal = ref(false);
const loading = ref(false);
const laborCost = ref<LaborCostRow | null>(null);
const wageRows = ref<CommonWageListRow[]>([]);

const yearMonth = computed(
  () => `${props.year}${String(props.month).padStart(2, '0')}`
);

const formatYearMonth = (ym?: string) => {
  if (!ym || ym.length < 6) return '-';
  return `${ym.slice(0, 4)}.${ym.slice(4, 6)}`;
};

const formatNumber = (num?: number | null) => {
  const n = Number(num) || 0;
  return n.toLocaleString();
};

const isDirectorJob = (job?: string) => {
  if (!job) return false;
  return job.includes('센터장') || job.includes('시설장');
};

const resolveJobCategory = (row: CommonWageListRow): string => {
  const job = (row.ce_position || '').trim() || '미분류';
  return formatLaborCostJobCategory(job);
};

const jobSummaryRows = computed<JobSummaryRow[]>(() => {
  const map = new Map<string, JobSummaryRow>();

  wageRows.value.forEach((row) => {
    const job = (row.ce_position || '').trim() || '미분류';
    if (isDirectorJob(job)) return;
    if (getCommonWageLedgerGrossPay(row) <= 0) return;

    const category = resolveJobCategory(row);
    const grossPay = getCommonWageLedgerGrossPay(row);
    const retirement = getCommonWageLedgerRetirement(row);
    const employerInsurance = getCommonWageLedgerEmployerInsurance(row);

    const existing = map.get(category);
    if (existing) {
      existing.count += 1;
      existing.grossPay += grossPay;
      existing.employerInsurance += employerInsurance;
      existing.retirement += retirement;
    } else {
      map.set(category, {
        category,
        count: 1,
        grossPay,
        employerInsurance,
        retirement,
      });
    }
  });

  return Array.from(map.values()).sort((a, b) => {
    const orderA = getCommonLaborCostJobCategoryOrder(a.category);
    const orderB = getCommonLaborCostJobCategoryOrder(b.category);
    if (orderA !== orderB) return orderA - orderB;
    return a.category.localeCompare(b.category, 'ko');
  });
});

const jobSummaryTotals = computed(() => {
  return jobSummaryRows.value.reduce(
    (acc, row) => {
      acc.count += row.count;
      acc.grossPay += row.grossPay;
      acc.employerInsurance += row.employerInsurance;
      acc.retirement += row.retirement;
      return acc;
    },
    { count: 0, grossPay: 0, employerInsurance: 0, retirement: 0 }
  );
});

const loadData = async () => {
  if (!props.memberCode) {
    alert('시설 코드가 없습니다.');
    return;
  }

  loading.value = true;
  laborCost.value = null;
  wageRows.value = [];

  const settled = await Promise.allSettled([
    ltcsApi.getLaborCost(props.memberCode, yearMonth.value, props.bizGubun || ''),
    commonWageApi.getList(props.memberCode, {
      year: props.year,
      month: props.month,
      bizGubun: props.bizGubun || '',
    }),
  ]);

  const [laborSettled, wageSettled] = settled;
  const failures: string[] = [];

  if (laborSettled.status === 'fulfilled') {
    if (laborSettled.value.data?.data) {
      laborCost.value = laborSettled.value.data.data;
    }
  } else {
    failures.push('심사결정금액');
    console.error('인건비지출내역 조회 오류:', laborSettled.reason);
  }

  if (wageSettled.status === 'fulfilled') {
    if (wageSettled.value.data?.success) {
      wageRows.value = (wageSettled.value.data.data || []) as CommonWageListRow[];
    }
  } else {
    failures.push('종사자 집계');
    console.error('인건비지출내역 임금 조회 오류:', wageSettled.reason);
  }

  loading.value = false;

  if (failures.length > 0 && !laborCost.value && wageRows.value.length === 0) {
    alert(`인건비지출내역 조회 실패: ${failures.join(', ')}`);
  }
};

const open = async () => {
  showModal.value = true;
  await loadData();
};

const close = () => {
  showModal.value = false;
};

defineExpose({ open, close });
</script>

<template>
  <Teleport to="body">
    <div v-if="showModal" class="home-labor-cost-modal-overlay" @click.self="close">
      <div class="home-labor-cost-modal">
        <div class="modal-header">
          <h3>인건비 지출내역 ({{ year }}년 {{ month }}월)</h3>
          <button type="button" class="close-btn" @click="close">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </div>

        <div class="modal-body">
          <LoadingOverlay :show="loading" text="불러오는 중..." />

          <template v-if="!loading">
            <div class="labor-cost-section">
              <div v-if="laborCost" class="labor-cost-card">
                <div class="labor-cost-title-bar">
                  ({{ formatYearMonth(laborCost.yearMonth || yearMonth) }})
                  {{ laborCost.facilityType || '-' }} 심사결정금액
                </div>
                <table class="labor-cost-table">
                  <thead>
                    <tr class="header-row-1">
                      <th colspan="2">① 장기요양급여비용(②+③+④)</th>
                      <th colspan="2">② 인건비 비율 반영</th>
                      <th colspan="2">③ 인건비 비율 미반영</th>
                      <th>④ 본인부담금</th>
                    </tr>
                    <tr class="header-row-2">
                      <th>비용</th>
                      <th>가산</th>
                      <th>비용</th>
                      <th>가산</th>
                      <th>비용</th>
                      <th>가산</th>
                      <th>비용</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td class="amount">{{ formatNumber(laborCost.totalCost) }}</td>
                      <td class="amount">{{ formatNumber(laborCost.totalAdd) }}</td>
                      <td class="amount highlight">{{ formatNumber(laborCost.laborCost) }}</td>
                      <td class="amount highlight">{{ formatNumber(laborCost.laborAdd) }}</td>
                      <td class="amount">{{ formatNumber(laborCost.nonLaborCost) }}</td>
                      <td class="amount">{{ formatNumber(laborCost.nonLaborAdd) }}</td>
                      <td class="amount">{{ formatNumber(laborCost.selfPayCost) }}</td>
                    </tr>
                  </tbody>
                </table>
                <div class="labor-cost-summary">
                  <div class="summary-item">
                    <span class="label">인건비 반영 합계</span>
                    <span class="value">
                      {{ formatNumber((laborCost.laborCost || 0) + (laborCost.laborAdd || 0)) }}원
                    </span>
                  </div>
                </div>
              </div>
              <div v-else class="no-data-message">
                <v-icon size="40" color="grey-lighten-2">mdi-file-document-outline</v-icon>
                <p>해당 조건의 인건비지출내역이 없습니다.</p>
              </div>
            </div>

            <div class="job-summary-section">
              <div class="job-summary-title">종사자구분별 집계</div>
              <div v-if="jobSummaryRows.length > 0" class="job-summary-card">
                <table class="job-summary-table">
                  <thead>
                    <tr>
                      <th>종사자구분</th>
                      <th>인원수</th>
                      <th>임금총액</th>
                      <th>사회보험 기관부담금총액</th>
                      <th>퇴직적립금 총액</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in jobSummaryRows" :key="row.category">
                      <td class="job-category-cell">{{ row.category }}</td>
                      <td class="amount center">{{ row.count }}명</td>
                      <td class="amount">{{ formatNumber(row.grossPay) }}</td>
                      <td class="amount">{{ formatNumber(row.employerInsurance) }}</td>
                      <td class="amount">{{ formatNumber(row.retirement) }}</td>
                    </tr>
                    <tr class="total-row">
                      <td>합계</td>
                      <td class="amount center">{{ jobSummaryTotals.count }}명</td>
                      <td class="amount">{{ formatNumber(jobSummaryTotals.grossPay) }}</td>
                      <td class="amount">{{ formatNumber(jobSummaryTotals.employerInsurance) }}</td>
                      <td class="amount">{{ formatNumber(jobSummaryTotals.retirement) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="no-data-message compact">
                <p>저장된 급여 데이터가 없습니다.</p>
              </div>
            </div>
          </template>
        </div>

        <div class="modal-footer">
          <v-btn variant="text" class="btn-close" @click="close">닫기</v-btn>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss">
@import '@/assets/styles/components/home-labor-cost-modal';
</style>
